import { createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../../api/api";
import { setLoading } from "../../loading/loadingSlice";

const BASE = "/api/v1/cc/nursing_care";

// ─── SERVICE ────────────────────────────────────────────────────────────────

export const fetchServices = createAsyncThunk(
    "manageService/fetchServices",
    async (params = {}, { dispatch, rejectWithValue }) => {
        const { page = 1, limit = 10, search = "" } = params;
        try {
            dispatch(setLoading(true));
            const response = await api.get(`${BASE}/service`, {
                params: { isActive: true, page, limit, search },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error fetching services"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const createService = createAsyncThunk(
    "manageService/createService",
    async (body, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.post(`${BASE}/service`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error creating service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const updateService = createAsyncThunk(
    "manageService/updateService",
    async ({ id, body }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.patch(`${BASE}/service/${id}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error updating service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const deleteService = createAsyncThunk(
    "manageService/deleteService",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            await api.delete(`${BASE}/service/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error deleting service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

// ─── SUB SERVICE ─────────────────────────────────────────────────────────────

export const fetchSubServices = createAsyncThunk(
    "manageService/fetchSubServices",
    async (serviceId, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.get(`${BASE}/service/sub_service`, {
                params: { service: serviceId },
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error fetching sub services"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const createSubService = createAsyncThunk(
    "manageService/createSubService",
    async (body, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.post(`${BASE}/service/sub_service`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error creating sub service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const updateSubService = createAsyncThunk(
    "manageService/updateSubService",
    async ({ id, body }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.patch(`${BASE}/service/sub_service/${id}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error updating sub service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const deleteSubService = createAsyncThunk(
    "manageService/deleteSubService",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            await api.delete(`${BASE}/service/sub_service/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error deleting sub service"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

// ─── SERVICE ITEM ─────────────────────────────────────────────────────────────

export const fetchServiceItems = createAsyncThunk(
    "manageService/fetchServiceItems",
    async (params = {}, { dispatch, rejectWithValue }) => {
        const { serviceId, subServiceId, page = 1, limit = 10, search = "" } = params;
        try {
            dispatch(setLoading(true));
            const queryParams = { page, limit, search };
            if (serviceId) queryParams.service = serviceId;
            if (subServiceId) queryParams.subService = subServiceId;

            const response = await api.get(`${BASE}/service/service_item`, {
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error fetching service items"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const getServiceItemById = createAsyncThunk(
    "manageService/getServiceItemById",
    async (params, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const queryParams = {};
            if (params._id) queryParams._id = params._id;
            if (params.service) queryParams.service = params.service;
            if (params.subService) queryParams.subService = params.subService;
            
            const response = await api.get(`${BASE}/service/service_item`, {
                params: queryParams,
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error fetching service item"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const createServiceItem = createAsyncThunk(
    "manageService/createServiceItem",
    async (body, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.post(`${BASE}/service/service_item`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error creating service item"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const updateServiceItem = createAsyncThunk(
    "manageService/updateServiceItem",
    async ({ id, body }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            const response = await api.patch(`${BASE}/service/service_item/${id}`, body);
            return response.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error updating service item"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);

export const deleteServiceItem = createAsyncThunk(
    "manageService/deleteServiceItem",
    async (id, { dispatch, rejectWithValue }) => {
        try {
            dispatch(setLoading(true));
            await api.delete(`${BASE}/service/service_item/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Error deleting service item"
            );
        } finally {
            dispatch(setLoading(false));
        }
    }
);
